import { PnoePhase, toNumber } from "../../entities/pnoeMeasurement";

/**
 * `"last_meal_2_4h"` → `"Last meal 2 4h"`.
 *
 * Only a fallback: the API's configuration endpoints carry proper labels for
 * every catalogued option. This keeps a raw enum readable when the config call
 * is unavailable, rather than showing snake_case in the UI.
 */
export const titleCaseOption = (value: string): string => {
  const spaced = value.replace(/[_-]+/g, " ").trim();
  if (!spaced) return value;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * A phase's load as one string: `"40 W"` for cycle protocols, `"3.7 km/h · 2%"`
 * for treadmill ones. Shared by the Phases table and the chart tooltip so the
 * two can't drift.
 */
export const formatPhaseLoad = (phase: PnoePhase): string => {
  const watts = toNumber(phase.watts);
  if (watts !== null) return `${watts} W`;
  const speed = toNumber(phase.speed);
  const grade = toNumber(phase.grade);
  if (speed === null && grade === null) return "—";
  return [
    speed === null ? null : `${speed} km/h`,
    grade === null ? null : `${grade}%`,
  ]
    .filter(Boolean)
    .join(" · ");
};

/**
 * Whether a phase carries a load worth showing.
 *
 * Recovery phases store zeros (`0 W`, or `0 km/h · 0%`), which says nothing —
 * so the tooltip omits the load rather than printing a row of noughts. Keyed on
 * the values, not the phase name, since names come from the backend.
 */
export const hasPhaseLoad = (phase: PnoePhase): boolean =>
  [phase.watts, phase.speed, phase.grade].some((raw) => {
    const value = toNumber(raw);
    return value !== null && value !== 0;
  });
